import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Sparkles, FileText } from 'lucide-react';
import SimulatorPage from './simulator/SimulatorPage';
import AIFullReportPage from './AIFullReportPage';

/**
 * AIInsightsPage — Iter163.
 *
 * Fusionne les deux entrées du menu Pilotage (« Diagnostic IA » et
 * « Rapport IA approfondi ») dans une seule page unifiée avec un
 * sélecteur clair en haut. Plus de redondance dans la nav.
 *
 * Sélection persistée via `?vue=diagnostic|rapport` pour conserver les
 * liens partagés des deux pages.
 */
export default function AIInsightsPage() {
    const [params, setParams] = useSearchParams();
    const initial = params.get('vue') === 'rapport' ? 'rapport' : 'diagnostic';
    const [view, setView] = useState(initial);
    const navigate = useNavigate();

    const switchTo = (v) => {
        setView(v);
        const next = new URLSearchParams(params);
        next.set('vue', v);
        setParams(next, { replace: true });
    };

    const SegmentedControl = (
        <div className="border-b border-slate-200 bg-white sticky top-0 z-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
                <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-[#681793]" />
                    <h1 className="text-sm font-semibold tracking-wider uppercase text-slate-500">
                        Intelligence IA
                    </h1>
                </div>
                <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1"
                     role="tablist" aria-label="Vues Intelligence IA">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={view === 'diagnostic'}
                        data-testid="ai-insights-tab-diagnostic"
                        onClick={() => switchTo('diagnostic')}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition ${
                            view === 'diagnostic'
                                ? 'bg-white text-[#681793] shadow-sm border border-slate-200'
                                : 'text-slate-500 hover:text-slate-800'
                        }`}>
                        <Sparkles className="w-4 h-4" />
                        Diagnostic IA
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={view === 'rapport'}
                        data-testid="ai-insights-tab-rapport"
                        onClick={() => switchTo('rapport')}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition ${
                            view === 'rapport'
                                ? 'bg-white text-[#681793] shadow-sm border border-slate-200'
                                : 'text-slate-500 hover:text-slate-800'
                        }`}>
                        <FileText className="w-4 h-4" />
                        Rapport approfondi
                    </button>
                </div>
                <p className="text-xs text-slate-500 mt-3 max-w-2xl">
                    {view === 'diagnostic'
                        ? "Diagnostic rapide : identifie les leviers d'amélioration immédiats sur votre activité."
                        : "Rapport long format : analyse approfondie multi-dimensionnelle, à partager avec votre équipe."}
                </p>
            </div>
        </div>
    );

    return (
        <div data-testid="ai-insights-page">
            {SegmentedControl}
            {view === 'diagnostic' ? <SimulatorPage embedded /> : <AIFullReportPage embedded />}
        </div>
    );
}
